import type { UseFormReturn } from 'react-hook-form';
import { Wand2 } from 'lucide-react';
import { useSipManipulationsForDropdown } from '../../../../hooks/useSipManipulations';
import type { SipPeerFormData } from '../schema';
import { NotYetEnforcedNotice } from '../../../common/badges';

/** Manipulations tab: peer-level manipulation precedence and attachments. */
export function ManipulationsTab({ form }: { form: UseFormReturn<SipPeerFormData> }) {
  const { data: manipulations, isLoading: manipulationsLoading } = useSipManipulationsForDropdown();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Peer manipulation precedence</h3>
        <p className="text-sm text-gray-600 mb-4">
          Interface pre-classification runs first, then peer inbound on ingress, and peer outbound runs last on egress.
        </p>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900 flex items-center">
            <Wand2 className="w-5 h-5 mr-2 text-purple-600" />
            Peer manipulation attachments
          </h4>
          <NotYetEnforcedNotice />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Peer inbound manipulation
            </label>
            <select
              {...form.register('inboundManipulationId')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={manipulationsLoading}
            >
              <option value="">None</option>
              {manipulations?.data?.filter((m) => m.enabled).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.ruleCount} rules)
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Applied after interface pre-classification and before routing rule overrides.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Peer outbound manipulation
            </label>
            <select
              {...form.register('outboundManipulationId')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={manipulationsLoading}
            >
              <option value="">None</option>
              {manipulations?.data?.filter((m) => m.enabled).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.ruleCount} rules)
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Applied after peer inbound and before route outbound overrides.</p>
          </div>
        </div>

        {manipulationsLoading && (
          <p className="text-xs text-gray-500">Loading manipulation profiles...</p>
        )}
      </div>
    </div>
  );
}
